/**
 * SavedRoutesService — desktop-plan, mobile-load trip templates
 *
 * Saves route-planning *inputs* (origin, destination, dates, RV specs) --
 * not the computed route -- so loading one always recomputes against
 * current wind/elevation/road-quality data instead of a stale snapshot.
 *
 * Loads saved routes from DynamoDB on sign-in, caches locally.
 *
 * API: GET/POST/DELETE /api/saved-routes (auth required)
 */

import { authFetch } from './authFetch.js';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

export class SavedRoutesService {
    constructor() {
        this.savedRoutes = [];
        this.loaded = false;
    }

    /**
     * Load saved routes from server. Call on sign-in.
     */
    async load() {
        try {
            const response = await authFetch(`${API_URL}/api/saved-routes`);
            if (!response.ok) {
                if (window.showToast) window.showToast(`Saved routes load failed: HTTP ${response.status}`, { type: 'warning' });
                return;
            }
            const data = await response.json();
            if (data.status === 'success') {
                this.savedRoutes = data.savedRoutes || [];
                this.loaded = true;
            } else if (window.showToast) {
                window.showToast(`Saved routes load failed: ${data.message || JSON.stringify(data)}`, { type: 'warning' });
            }
        } catch (err) {
            console.warn('Failed to load saved routes:', err.message);
            if (window.showToast) window.showToast(`Saved routes load exception: ${err.message}`, { type: 'warning' });
        }
    }

    /**
     * Get all saved routes.
     */
    getAll() {
        return this.savedRoutes;
    }

    /**
     * Save a new route. `inputs` is the plan-form field shape from
     * getRoutePlanInputs() in main.js (origin, originWasGps, destination,
     * departureDate, departureTime, rvType, height, weight, length).
     */
    async add(name, inputs) {
        try {
            const response = await authFetch(`${API_URL}/api/saved-routes`, {
                method: 'POST',
                body: JSON.stringify({ name, ...inputs })
            });
            const data = await response.json();
            if (data.status === 'success') {
                this.savedRoutes.push(data.route);
                return data.route;
            }
            console.error('Saved route add failed:', data);
            alert('Save error: ' + (data.message || JSON.stringify(data)));
        } catch (err) {
            console.error('Failed to add saved route:', err.message);
            alert('Save exception: ' + err.message);
        }
        return null;
    }

    /**
     * Delete a saved route.
     */
    async delete(id) {
        try {
            const response = await authFetch(`${API_URL}/api/saved-routes`, {
                method: 'DELETE',
                body: JSON.stringify({ id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                this.savedRoutes = this.savedRoutes.filter(r => r.id !== id);
                return true;
            }
            // A non-exception failure (4xx/5xx with a valid JSON body) was
            // previously silent here -- looked exactly like the delete
            // button doing nothing, with no way to tell why.
            console.error('Saved route delete failed:', data);
        } catch (err) {
            console.error('Failed to delete saved route:', err.message);
        }
        return false;
    }
}

export const savedRoutesService = new SavedRoutesService();
